// src/components/UI/ProjectCard.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Project, FeaturedProject } from '../../types';

// Create a union type for the component
type ProjectCardProject = Project | FeaturedProject;

interface ProjectCardProps {
  project: ProjectCardProject;
  featured?: boolean;
  showDetails?: boolean;
  compact?: boolean;
  interactive?: boolean;
  onCardClick?: (project: ProjectCardProject) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ 
  project, 
  featured = false,
  showDetails = false,
  compact = false,
  interactive = true,
  onCardClick
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Safe property access with defaults
  const projectSlug = 'slug' in project ? project.slug : `project-${project.id}`;
  const projectProgress = 'progress' in project ? project.progress : 50;
  const projectStatus = 'status' in project ? project.status : 'active';
  const projectImpact = 'impact' in project ? project.impact : 'Making positive impact';
  const projectLocation = 'location' in project ? project.location : 'Various communities';
  const projectStartDate = 'startDate' in project && typeof project.startDate === 'string' ? project.startDate : '';
  const projectEndDate = 'endDate' in project && typeof project.endDate === 'string' ? project.endDate : '';
  const projectVolunteers = 'volunteersCount' in project ? Number(project.volunteersCount) : 0;
  const projectBudget = 'budget' in project ? project.budget : null;
  const projectPartners = 'partners' in project ? project.partners : [];

  const getCategoryConfig = (category: string) => {
    const configs = {
      food_security: { 
        icon: '🌱', 
        color: 'from-green-500 to-emerald-600',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
      },
      climate_action: { 
        icon: '🌍', 
        color: 'from-blue-500 to-cyan-600',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200'
      },
      youth_empowerment: { 
        icon: '👨‍🎓', 
        color: 'from-orange-500 to-amber-600',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-200'
      },
      women_empowerment: { 
        icon: '👩‍🌾', 
        color: 'from-purple-500 to-pink-600',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-700',
        borderColor: 'border-purple-200'
      },
      education: { 
        icon: '📚', 
        color: 'from-red-500 to-rose-600',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200'
      },
      healthcare: { 
        icon: '🏥', 
        color: 'from-teal-500 to-cyan-600',
        bgColor: 'bg-teal-50',
        textColor: 'text-teal-700',
        borderColor: 'border-teal-200'
      }
    };
    
    return configs[category as keyof typeof configs] || { 
      icon: '📋', 
      color: 'from-gray-500 to-gray-600',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200'
    };
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      active: { 
        color: 'bg-green-500 text-white',
        glow: 'shadow-lg shadow-green-500/25',
        text: 'Active',
        icon: '🟢'
      },
      completed: { 
        color: 'bg-blue-500 text-white',
        glow: 'shadow-lg shadow-blue-500/25',
        text: 'Completed',
        icon: '✅'
      },
      upcoming: { 
        color: 'bg-amber-500 text-white',
        glow: 'shadow-lg shadow-amber-500/25',
        text: 'Upcoming',
        icon: '⏳'
      },
      planning: { 
        color: 'bg-purple-500 text-white',
        glow: 'shadow-lg shadow-purple-500/25',
        text: 'Planning',
        icon: '📋'
      },
      paused: { 
        color: 'bg-gray-500 text-white',
        glow: 'shadow-lg shadow-gray-500/25',
        text: 'Paused',
        icon: '⏸️'
      }
    };
    
    return configs[status as keyof typeof configs] || configs.active;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 100) return 'bg-amber-500';
    if (progress >= 100) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressVariant = (progress: number) => {
    if (progress >= 100) return 'success';
    if (progress >= 100) return 'warning';
    if (progress >= 100) return 'caution';
    return 'danger';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatBudget = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount}`;
  };

  const categoryConfig = getCategoryConfig(project.category);
  const statusConfig = getStatusConfig(projectStatus ?? 'active');
  const progressColor = getProgressColor(projectProgress ?? 0);
  const progressVariant = getProgressVariant(projectProgress ?? 0);

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    hover: interactive ? {
      y: -8,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17
      }
    } : {},
    tap: interactive ? {
      scale: 0.98
    } : {}
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(project);
    }
  };

  const CardContent = (
    <motion.div
      className={`w-100 ${featured ? 'featured-project' : ''} ${compact ? 'compact-project' : ''}`}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      whileTap="tap"
      viewport={{ once: true, amount: 0.3 }}
      onClick={handleCardClick}
    >
      <div className={`card h-100 border-0 overflow-hidden position-relative ${
        featured ? 'shadow-lg featured-glow' : 'shadow-md'
      } ${interactive ? 'cursor-pointer' : ''} ${compact ? 'compact-card' : ''}`}
      style={{
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        {/* Featured Badge */}
        {featured && (
          <div className="position-absolute top-0 start-0 z-3 m-3">
            <span className="badge bg-warning text-dark fw-bold px-3 py-2 shadow">
              ⭐ Featured
            </span>
          </div>
        )}

        {/* Status Badge */}
        <div className={`position-absolute top-0 end-0 z-3 m-3 ${statusConfig.glow}`}>
          <span className={`badge ${statusConfig.color} fw-semibold px-3 py-2 d-flex align-items-center`}>
            <span className="me-1">{statusConfig.icon}</span>
            {statusConfig.text}
          </span>
        </div>

        {/* Image Section */}
        <div className="position-relative overflow-hidden" style={{ height: compact ? '160px' : '240px' }}>
          {project.image && !imageError ? (
            <>
              {/* Loading Skeleton */}
              {!imageLoaded && (
                <div 
                  className="position-absolute top-0 start-0 w-100 h-100 bg-light"
                  style={{
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'loading 1.5s infinite'
                  }}
                />
              )}
              
              <motion.img
                src={project.image.startsWith('http') || project.image.startsWith('/') ? project.image : `http://localhost/iga/backend/uploads/${project.image}`}
                alt={project.title}
                className="w-100 h-100 object-fit-cover"
                variants={imageVariants}
                initial="hidden"
                animate={imageLoaded ? "visible" : "hidden"}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                style={{
                  filter: imageLoaded ? 'none' : 'blur(10px)'
                }}
              />
            </>
          ) : (
            <div 
              className={`w-100 h-100 d-flex align-items-center justify-content-center ${categoryConfig.bgColor}`}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                className="text-center"
              >
                <span className={compact ? "display-4 opacity-50" : "display-1 opacity-50"}>
                  {categoryConfig.icon}
                </span>
              </motion.div>
            </div>
          )}

          {/* Gradient Overlay */}
          <div 
            className="position-absolute bottom-0 start-0 w-100 h-50"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)'
            }}
          />

          {/* Category Badge on Image */}
          <div className="position-absolute bottom-0 start-0 m-3">
            <span 
              className={`badge ${categoryConfig.bgColor} ${categoryConfig.textColor} border-0 fw-semibold px-3 py-2 ${compact ? 'small' : ''}`}
              style={{ borderRadius: '50px' }}
            >
              <span className="me-2">{categoryConfig.icon}</span>
              {project.category.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className={`card-body d-flex flex-column ${compact ? 'p-3' : 'p-4'}`}>
          {/* Title */}
          <motion.h5 
            className={`card-title fw-bold text-dark mb-3 line-clamp-2 ${compact ? 'h6' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={{
              lineHeight: '1.4',
              minHeight: compact ? '2.5rem' : '3.5rem'
            }}
          >
            {project.title}
          </motion.h5>

          {/* Description */}
          {!compact && (
            <motion.p 
              className="card-text text-muted flex-grow-1 mb-4 line-clamp-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              style={{
                lineHeight: '1.6',
                fontSize: '0.95rem'
              }}
            >
              {project.description.length > 120 
                ? `${project.description.substring(0, 120)}...` 
                : project.description
              }
            </motion.p>
          )}

          {/* Extended Details */}
          {showDetails && !compact && (
            <motion.div 
              className="mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {/* Location & Date */}
              <div className="row g-2 mb-3">
                <div className="col-12">
                  <div className="d-flex align-items-center text-muted small">
                    <span className="me-2">📍</span>
                    <span>{projectLocation}</span>
                  </div>
                </div>
                {projectStartDate !== '' && (
                  <div className="col-6">
                    <div className="d-flex align-items-center text-muted small">
                      <span className="me-2">📅</span>
                      <span>Start: {formatDate(projectStartDate)}</span>
                    </div>
                  </div>
                )}
                {projectEndDate !== '' && (
                  <div className="col-6">
                    <div className="d-flex align-items-center text-muted small">
                      <span className="me-2">🎯</span>
                      <span>End: {formatDate(projectEndDate)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Row */}
              <div className="row g-3">
                {projectVolunteers > 0 && (
                  <div className="col-4">
                    <div className="text-center">
                      <div className="fw-bold text-dark">{projectVolunteers}</div>
                      <div className="text-muted small">Volunteers</div>
                    </div>
                  </div>
                )}
                {projectBudget && (
                  <div className="col-4">
                    <div className="text-center">
                      <div className="fw-bold text-dark">{formatBudget(projectBudget)}</div>
                      <div className="text-muted small">Budget</div>
                    </div>
                  </div>
                )}
                {Array.isArray(projectPartners) && projectPartners.length > 0 && (
                  <div className="col-4">
                    <div className="text-center">
                      <div className="fw-bold text-dark">{projectPartners.length}</div>
                      <div className="text-muted small">Partners</div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Progress Section */}
          {!compact && (
            <motion.div 
              className="mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <span className="text-muted small fw-semibold me-2">Progress</span>
                  <span className={`badge ${progressColor} text-white small`}>
                    {progressVariant}
                  </span>
                </div>
                <span className={`fw-bold ${
                  (projectProgress ?? 0) >= 80 ? 'text-success' : 
                  (projectProgress ?? 0) >= 50 ? 'text-warning' : 
                  'text-danger'
                }`}>
                  {projectProgress}%
                </span>
              </div>
              
              {/* Animated Progress Bar */}
              <div 
                className="progress rounded-pill overflow-hidden" 
                style={{ height: '8px', backgroundColor: 'rgba(0,0,0,0.1)' }}
              >
                <motion.div
                  className={`progress-bar rounded-pill ${progressColor}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${projectProgress}%` }}
                  transition={{ 
                    duration: 1.5, 
                    delay: 0.7,
                    ease: "easeOut"
                  }}
                />
              </div>
            </motion.div>
          )}

          {/* CTA Button */}
          {interactive && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: compact ? 0.4 : 0.8 }}
            >
              <Link 
                to={`/programs/${projectSlug}`}
                className={`btn w-100 fw-semibold position-relative overflow-hidden ${compact ? 'btn-sm' : ''}`}
                style={{
                  background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: compact ? '8px 16px' : '12px 24px',
                  color: 'white'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="position-relative z-2 d-flex align-items-center justify-content-center">
                  {compact ? 'View' : 'Explore Project'}
                  <motion.span
                    className="ms-2"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
                
                {/* Hover Effect */}
                <motion.div
                  className="position-absolute top-0 left-0 w-100 h-100 bg-white bg-opacity-20"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
              </Link>
            </motion.div>
          )}
        </div>

        {/* Impact Badge */}
        {!compact && (
          <motion.div 
            className="position-absolute bottom-0 end-0 m-3"
            initial={{ scale: 0, rotate: 180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, delay: 1 }}
          >
            <div className="bg-dark bg-opacity-75 text-white rounded-pill px-3 py-1 small fw-semibold backdrop-blur">
              💫 {projectImpact}
            </div>
          </motion.div>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        .object-fit-cover {
          object-fit: cover;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .featured-glow {
          box-shadow: 0 20px 40px rgba(25, 135, 84, 0.15), 0 0 0 1px rgba(25, 135, 84, 0.1) !important;
        }
        
        .featured-project .card {
          border: 2px solid #198754 !important;
        }
        
        .backdrop-blur {
          backdrop-filter: blur(10px);
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .compact-card .card-body {
          padding: 1rem !important;
        }
        
        .compact-project {
          max-width: 320px;
        }
        
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </motion.div>
  );

  return CardContent;
};

export default ProjectCard;